import { useState } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import BixButton from '$/components/global/bixButton';
import Modal from '$/components/global/modal';
import { toastError, toastSuccess } from '$/store/toast/actions';
import { ComplianceService } from '$/services';
import * as Bixby from '$/core';
import './index.scss';
import { ColumnState } from 'ag-grid-enterprise';

const GetFormattedDeclarationStatus = (declarationStatus: Bixby.Enums.ClientCompanyDeclarationStatus) => {
  switch (declarationStatus) {
    case Bixby.Enums.ClientCompanyDeclarationStatus.Pending:
      return 'Pending';
    case Bixby.Enums.ClientCompanyDeclarationStatus.ClientApproved:
      return 'Pending Bixby Approval';
    case Bixby.Enums.ClientCompanyDeclarationStatus.Rejected:
      return 'Rejected';
    case Bixby.Enums.ClientCompanyDeclarationStatus.BixbyApproved:
      return 'Approved';
    default:
      return declarationStatus;
  }
};

interface IDeclarationStatusStringProps {
  declarationStatus: Bixby.Enums.ClientCompanyDeclarationStatus;
}

const DeclarationStatusString: React.FC<IDeclarationStatusStringProps> = ({ declarationStatus }) => {
  const formattedDeclarationStatus = GetFormattedDeclarationStatus(declarationStatus);
  return <span>{formattedDeclarationStatus}</span>;
};

interface ApprovalModalState {
  companyId: string;
  bixbyId: string;
  dataSiteProviderName: string;
  clientId: string | undefined;
  clientName: string | undefined;
  createCompany: boolean;
}

interface IPrivateDeclarationsProps {
  companyDeclarations: Bixby.Models.AdminCompanyDeclarationViewModel[];
  handleFetchCompanies: () => Promise<void>;
  dispatchToastError: (message: string) => Promise<void>;
  dispatchToastSuccess: (message: string) => Promise<void>;
}

const PrivateDeclarationsGrid: React.FC<IPrivateDeclarationsProps> = ({
  companyDeclarations,
  handleFetchCompanies,
  dispatchToastError,
  dispatchToastSuccess,
}) => {
  const [creatingPrivateCompany, setCreatingPrivateCompany] = useState(false);
  const [approvalModalState, setApprovalModalState] = useState(undefined as ApprovalModalState | undefined);
  const [applyingDeclaration, setApplyingDeclaration] = useState(
    undefined as Bixby.Enums.ClientCompanyDeclarationStatus | undefined
  );
  const [declarationReason, setDeclarationReason] = useState('');

  const openApplyDeclarationModal = (declarationRow: ApprovalModalState) => {
    if (
      !declarationRow.companyId ||
      !declarationRow.clientId ||
      !declarationRow.bixbyId ||
      !declarationRow.dataSiteProviderName
    ) {
      return;
    }
    setApprovalModalState(declarationRow);
    setDeclarationReason('');
  };

  const handleCreatePrivateCompany = async (companyId: string) => {
    setCreatingPrivateCompany(true);
    try {
      await ComplianceService.createPrivateCompany(companyId);
      await handleFetchCompanies();
    } catch (err) {
      dispatchToastError('Unable to create private company');
    } finally {
      setCreatingPrivateCompany(false);
      setApprovalModalState(undefined);
    }
  };

  const handleApplyDeclaration = async (
    decision: Bixby.Enums.ClientCompanyDeclarationStatus,
    declarationReason?: string
  ) => {
    if (!approvalModalState) return;
    try {
      setApplyingDeclaration(decision);
      if (decision === Bixby.Enums.ClientCompanyDeclarationStatus.BixbyApproved) {
        await ComplianceService.approveDeclarationByAdmin(
          approvalModalState.clientId ?? '',
          approvalModalState.companyId,
          declarationReason
        );
        dispatchToastSuccess('Declaration approved');
      } else if (decision === Bixby.Enums.ClientCompanyDeclarationStatus.Rejected) {
        await ComplianceService.rejectDeclarationByAdmin(
          approvalModalState.clientId ?? '',
          approvalModalState.companyId,
          declarationReason
        );
        dispatchToastSuccess('Declaration rejected');
      }
      await handleFetchCompanies();
    } catch (err) {
      dispatchToastError('Unable to approve declaration');
    } finally {
      setApplyingDeclaration(undefined);
      setApprovalModalState(undefined);
    }
  };

  return (
    <div className="private_declaration_grid">
      <div className="grid_container">
        <div className="grid ag-theme-alpine">
          <AgGridReact
            enableRangeSelection={true}
            defaultColDef={{
              flex: 1,
              minWidth: 100,
              sortable: true,
              resizable: true,
              lockPinned: true,
              cellClass: 'align-left',
            }}
            onGridReady={params => {
              const defaultSortModel: ColumnState[] = [
                { colId: 'clientCompanyDeclaration.declarationStatus', sort: 'desc', sortIndex: 0 },
                { colId: 'clientCompanyDeclaration.declarationRequestedDate', sort: 'asc', sortIndex: 1 },
                { colId: 'clientCompanyDeclaration.declarationRequestedBy_Email', sort: 'asc', sortIndex: 2 },
              ];

              params.api.applyColumnState({ state: defaultSortModel });
            }}
            rowData={companyDeclarations}
            suppressDragLeaveHidesColumns={true}
            suppressMovableColumns={true}
            animateRows={true}
            rowHeight={36}
            headerHeight={36}
            columnDefs={[
              {
                maxWidth: 220,
                field: 'clientCompanyDeclaration.declarationStatus',
                headerName: 'Status',
                cellRenderer: params =>
                  params.data?.privateCompanyExists === false &&
                  params.data?.clientCompanyDeclaration.declarationStatus ===
                    Bixby.Enums.ClientCompanyDeclarationStatus.ClientApproved ? (
                    <BixButton
                      useSmall
                      type="button"
                      className="red_button"
                      onClick={() =>
                        openApplyDeclarationModal({
                          companyId: params.data?.companyId,
                          bixbyId: params.data?.bixbyId,
                          dataSiteProviderName: params.data?.dataSiteProviderName,
                          clientId: params.data.clientId,
                          clientName: params.data.clientName,
                          createCompany: true,
                        })
                      }>
                      Create Company
                    </BixButton>
                  ) : params.data?.clientCompanyDeclaration.declarationStatus ===
                    Bixby.Enums.ClientCompanyDeclarationStatus.ClientApproved ? (
                    <BixButton
                      useSmall
                      type="button"
                      onClick={() =>
                        openApplyDeclarationModal({
                          companyId: params.data?.companyId,
                          bixbyId: params.data?.bixbyId,
                          dataSiteProviderName: params.data?.dataSiteProviderName,
                          clientId: params.data.clientId,
                          clientName: params.data.clientName,
                          createCompany: false,
                        })
                      }>
                      <DeclarationStatusString
                        declarationStatus={params.data?.clientCompanyDeclaration.declarationStatus}
                      />
                    </BixButton>
                  ) : (
                    <DeclarationStatusString
                      declarationStatus={params.data?.clientCompanyDeclaration.declarationStatus}
                    />
                  ),
                // sort by Pending Bixby Approval
                valueGetter: params => {
                  let result = '';

                  if (
                    params.data?.privateCompanyExists === false &&
                    params.data?.clientCompanyDeclaration.declarationStatus ===
                      Bixby.Enums.ClientCompanyDeclarationStatus.ClientApproved
                  ) {
                    result = 'Create Company';
                  } else if (params.data?.clientCompanyDeclaration.declarationStatus) {
                    result = GetFormattedDeclarationStatus(params.data.clientCompanyDeclaration.declarationStatus);
                  }

                  return result;
                },
              },
              {
                field: 'bixbyId',
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                headerName: 'BixbyId',
                cellRenderer: (params: any) => {
                  const url = '/admin/company/' + params.data.companyId;
                  return <Link className="link_underline" to={url}>{` ${params.data.bixbyId}`}</Link>;
                },
              },
              {
                field: 'clientName',
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                headerName: 'Client Name',
                cellRenderer: (params: any) => {
                  const url = '/admin/client/' + params.data.clientId;
                  return <Link className="link_underline" to={url}>{` ${params.data.clientName}`}</Link>;
                },
              },
              {
                maxWidth: 220,
                field: 'clientCompanyDeclaration.declarationRequestedDate',
                headerName: 'Requested Date',
                valueFormatter: params =>
                  params.data?.clientCompanyDeclaration.declarationRequestedDate
                    ? dayjs(params.data?.clientCompanyDeclaration.declarationRequestedDate).format('MM/DD/YYYY')
                    : 'N/A',
              },
              {
                field: 'clientCompanyDeclaration.declarationRequestedBy_Email',
                headerName: 'Requested By',
                valueGetter: params => params.data?.clientCompanyDeclaration.declarationRequestedBy_Email,
              },
              {
                headerName: 'Notes',
                filter: false,
                wrapText: true,
                autoHeight: true,
                cellRenderer: params => (
                  <>
                    {params.data?.clientCompanyDeclaration.declarationClientReason && (
                      <p>{params.data?.clientCompanyDeclaration.declarationClientReason}</p>
                    )}
                    {params.data?.clientCompanyDeclaration.declarationBixbyReason && (
                      <p>{params.data?.clientCompanyDeclaration.declarationBixbyReason}</p>
                    )}
                  </>
                ),
              },
            ]}></AgGridReact>
        </div>
        <Modal
          title=""
          active={!!approvalModalState}
          hideClose={!!applyingDeclaration || !!creatingPrivateCompany}
          onClose={() => setApprovalModalState(undefined)}>
          <>
            {approvalModalState?.createCompany ? (
              <p>
                By clicking "Approve" below, you are creating a new MNPI Bixby ID for{' '}
                <span className="font-bold">{approvalModalState?.bixbyId}</span>.
              </p>
            ) : (
              <p>
                By clicking the "Approve" below, you acknowledge and agree that you have independently verified that{' '}
                <span className="font-bold">{approvalModalState?.clientName} </span> has access to{' '}
                <span className="font-bold"> {approvalModalState?.bixbyId} </span> through the data site{' '}
                <span className="font-bold">{approvalModalState?.dataSiteProviderName}</span>.
              </p>
            )}
            <div className="input_wrap">
              <label className="label" htmlFor="declarationReason">
                Declaration Reason
              </label>
              <textarea
                id="declarationReason"
                placeholder="Enter reason (optional)"
                value={declarationReason}
                onChange={e => setDeclarationReason(e.target.value)}
                className="input"
              />
            </div>
            <div className="button_side_wrap">
              <BixButton
                type="button"
                disabled={!!applyingDeclaration || !!creatingPrivateCompany}
                useLoading
                isLoading={
                  applyingDeclaration === Bixby.Enums.ClientCompanyDeclarationStatus.BixbyApproved ||
                  creatingPrivateCompany
                }
                onClick={() => {
                  if (approvalModalState?.createCompany) {
                    handleCreatePrivateCompany(approvalModalState.companyId);
                  } else {
                    handleApplyDeclaration(Bixby.Enums.ClientCompanyDeclarationStatus.BixbyApproved, declarationReason);
                  }
                }}>
                Approve
              </BixButton>
              <BixButton
                type="button"
                className="red_button"
                disabled={!!applyingDeclaration || !!creatingPrivateCompany}
                useLoading
                isLoading={applyingDeclaration === Bixby.Enums.ClientCompanyDeclarationStatus.Rejected}
                onClick={() =>
                  handleApplyDeclaration(Bixby.Enums.ClientCompanyDeclarationStatus.Rejected, declarationReason)
                }>
                Reject
              </BixButton>
            </div>
          </>
        </Modal>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(null, mapDispatchToProps)(PrivateDeclarationsGrid);
